import { z } from 'zod';

/**
 * 🇪🇺 EU-priority country list — Avrupa öncelikli, sonra global.
 * Her giriş: ISO 3166-1 alpha-2 + native name + önerilen currency + suggested locale + timezone.
 */
export interface CountryEntry {
  code: string;
  name: string;
  nativeName: string;
  currency: string;
  suggestedLocale: 'en' | 'de' | 'fr' | 'nl' | 'tr';
  timezone: string;
}

export const EU_COUNTRIES: readonly CountryEntry[] = [
  // EU members (alphabetical by code)
  { code: 'AT', name: 'Austria', nativeName: 'Österreich', currency: 'EUR', suggestedLocale: 'de', timezone: 'Europe/Vienna' },
  { code: 'BE', name: 'Belgium', nativeName: 'België / Belgique', currency: 'EUR', suggestedLocale: 'fr', timezone: 'Europe/Brussels' },
  { code: 'BG', name: 'Bulgaria', nativeName: 'България', currency: 'BGN', suggestedLocale: 'en', timezone: 'Europe/Sofia' },
  { code: 'CY', name: 'Cyprus', nativeName: 'Κύπρος', currency: 'EUR', suggestedLocale: 'en', timezone: 'Asia/Nicosia' },
  { code: 'CZ', name: 'Czech Republic', nativeName: 'Česko', currency: 'CZK', suggestedLocale: 'en', timezone: 'Europe/Prague' },
  { code: 'DE', name: 'Germany', nativeName: 'Deutschland', currency: 'EUR', suggestedLocale: 'de', timezone: 'Europe/Berlin' },
  { code: 'DK', name: 'Denmark', nativeName: 'Danmark', currency: 'DKK', suggestedLocale: 'en', timezone: 'Europe/Copenhagen' },
  { code: 'EE', name: 'Estonia', nativeName: 'Eesti', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Tallinn' },
  { code: 'ES', name: 'Spain', nativeName: 'España', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Madrid' },
  { code: 'FI', name: 'Finland', nativeName: 'Suomi', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Helsinki' },
  { code: 'FR', name: 'France', nativeName: 'France', currency: 'EUR', suggestedLocale: 'fr', timezone: 'Europe/Paris' },
  { code: 'GR', name: 'Greece', nativeName: 'Ελλάδα', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Athens' },
  { code: 'HR', name: 'Croatia', nativeName: 'Hrvatska', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Zagreb' },
  { code: 'HU', name: 'Hungary', nativeName: 'Magyarország', currency: 'HUF', suggestedLocale: 'en', timezone: 'Europe/Budapest' },
  { code: 'IE', name: 'Ireland', nativeName: 'Éire', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Dublin' },
  { code: 'IT', name: 'Italy', nativeName: 'Italia', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Rome' },
  { code: 'LT', name: 'Lithuania', nativeName: 'Lietuva', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Vilnius' },
  { code: 'LU', name: 'Luxembourg', nativeName: 'Lëtzebuerg', currency: 'EUR', suggestedLocale: 'fr', timezone: 'Europe/Luxembourg' },
  { code: 'LV', name: 'Latvia', nativeName: 'Latvija', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Riga' },
  { code: 'MT', name: 'Malta', nativeName: 'Malta', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Malta' },
  { code: 'NL', name: 'Netherlands', nativeName: 'Nederland', currency: 'EUR', suggestedLocale: 'nl', timezone: 'Europe/Amsterdam' },
  { code: 'PL', name: 'Poland', nativeName: 'Polska', currency: 'PLN', suggestedLocale: 'en', timezone: 'Europe/Warsaw' },
  { code: 'PT', name: 'Portugal', nativeName: 'Portugal', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Lisbon' },
  { code: 'RO', name: 'Romania', nativeName: 'România', currency: 'RON', suggestedLocale: 'en', timezone: 'Europe/Bucharest' },
  { code: 'SE', name: 'Sweden', nativeName: 'Sverige', currency: 'SEK', suggestedLocale: 'en', timezone: 'Europe/Stockholm' },
  { code: 'SI', name: 'Slovenia', nativeName: 'Slovenija', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Ljubljana' },
  { code: 'SK', name: 'Slovakia', nativeName: 'Slovensko', currency: 'EUR', suggestedLocale: 'en', timezone: 'Europe/Bratislava' },
  // Non-EU European (priority)
  { code: 'CH', name: 'Switzerland', nativeName: 'Schweiz / Suisse', currency: 'CHF', suggestedLocale: 'de', timezone: 'Europe/Zurich' },
  { code: 'GB', name: 'United Kingdom', nativeName: 'United Kingdom', currency: 'GBP', suggestedLocale: 'en', timezone: 'Europe/London' },
  { code: 'IS', name: 'Iceland', nativeName: 'Ísland', currency: 'EUR', suggestedLocale: 'en', timezone: 'Atlantic/Reykjavik' },
  { code: 'LI', name: 'Liechtenstein', nativeName: 'Liechtenstein', currency: 'CHF', suggestedLocale: 'de', timezone: 'Europe/Vaduz' },
  { code: 'NO', name: 'Norway', nativeName: 'Norge', currency: 'NOK', suggestedLocale: 'en', timezone: 'Europe/Oslo' },
  // Secondary markets
  { code: 'TR', name: 'Türkiye', nativeName: 'Türkiye', currency: 'TRY', suggestedLocale: 'tr', timezone: 'Europe/Istanbul' },
  { code: 'US', name: 'United States', nativeName: 'United States', currency: 'USD', suggestedLocale: 'en', timezone: 'America/New_York' },
];

export const SUPPORTED_COUNTRY_CODES = EU_COUNTRIES.map((c) => c.code) as readonly string[];

/**
 * 👤 Ülke koduna göre yaygın "John Doe" karşılığı placeholder name.
 * Form alanlarında "Alıcı ad-soyad" gibi yerlerde locale-uygun örnek.
 */
export function getPersonNameExampleForCountry(code: string | null | undefined): string {
  if (!code) return 'John Doe';
  const c = code.toUpperCase();
  const samples: Record<string, string> = {
    AT: 'Maximilian Müller',
    BE: 'Jan Janssens',
    CH: 'Hans Müller',
    CZ: 'Jan Novák',
    DE: 'Max Mustermann',
    DK: 'Lars Hansen',
    ES: 'Juan García',
    FI: 'Matti Korhonen',
    FR: 'Jean Dupont',
    GB: 'John Smith',
    HU: 'Kovács János',
    IE: 'John Murphy',
    IT: 'Mario Rossi',
    LU: 'Jean Schmit',
    NL: 'Jan Jansen',
    NO: 'Ola Nordmann',
    PL: 'Jan Kowalski',
    PT: 'João Silva',
    SE: 'Erik Andersson',
    TR: 'Ahmet Yılmaz',
  };
  return samples[c] ?? 'John Doe';
}

/**
 * 🏦 Ülke koduna göre IBAN örneği (format'ı tahmin için placeholder).
 * Gerçek validation `ibanSchema` ile yapılır — bu sadece UI ipucu.
 * Boşluklu format, kopyalama anında temizlenir.
 */
export function getIbanExampleForCountry(code: string | null | undefined): string {
  if (!code) return 'DE89 3704 0044 0532 0130 00';
  const c = code.toUpperCase();
  const samples: Record<string, string> = {
    AT: 'AT61 1904 3002 3457 3201',
    BE: 'BE68 5390 0754 7034',
    CH: 'CH93 0076 2011 6238 5295 7',
    CZ: 'CZ65 0800 0000 1920 0014 5399',
    DE: 'DE89 3704 0044 0532 0130 00',
    DK: 'DK50 0040 0440 1162 43',
    ES: 'ES91 2100 0418 4502 0005 1332',
    FI: 'FI21 1234 5600 0007 85',
    FR: 'FR14 2004 1010 0505 0001 3M02 606',
    GB: 'GB29 NWBK 6016 1331 9268 19',
    HU: 'HU42 1177 3016 1111 1018 0000 0000',
    IE: 'IE29 AIBK 9311 5212 3456 78',
    IT: 'IT60 X054 2811 1010 0000 0123 456',
    LU: 'LU28 0019 4006 4475 0000',
    NL: 'NL91 ABNA 0417 1643 00',
    NO: 'NO93 8601 1117 947',
    PL: 'PL61 1090 1014 0000 0712 1981 2874',
    PT: 'PT50 0002 0123 1234 5678 9015 4',
    SE: 'SE45 5000 0000 0583 9825 7466',
    TR: 'TR33 0006 1005 1978 6457 8413 26',
  };
  return samples[c] ?? 'DE89 3704 0044 0532 0130 00';
}

/**
 * 🏦 Ülke koduna göre BIC/SWIFT örneği (placeholder).
 * Genelde 8 veya 11 karakter — country position 5-6 ülke kodudur.
 */
export function getBicExampleForCountry(code: string | null | undefined): string {
  if (!code) return 'COBADEFFXXX';
  const c = code.toUpperCase();
  const samples: Record<string, string> = {
    AT: 'GIBAATWWXXX',
    BE: 'GEBABEBBXXX',
    CH: 'UBSWCHZH80A',
    CZ: 'CEKOCZPPXXX',
    DE: 'COBADEFFXXX',
    DK: 'DABADKKKXXX',
    ES: 'CAIXESBBXXX',
    FI: 'NDEAFIHHXXX',
    FR: 'SOGEFRPPXXX',
    GB: 'NWBKGB2LXXX',
    HU: 'OTPVHUHBXXX',
    IE: 'AIBKIE2DXXX',
    IT: 'UNCRITM1XXX',
    LU: 'BCEELULLXXX',
    NL: 'INGBNL2AXXX',
    NO: 'DNBANOKKXXX',
    PL: 'BPKOPLPWXXX',
    PT: 'BCOMPTPLXXX',
    SE: 'NDEASESSXXX',
    TR: 'ISBKTRISXXX',
  };
  return samples[c] ?? 'COBADEFFXXX';
}

export function getCountryByCode(code: string | null | undefined): CountryEntry | undefined {
  if (!code) return undefined;
  return EU_COUNTRIES.find((c) => c.code === code);
}

export const supportedCountryCodeSchema = z
  .string()
  .length(2)
  .refine((v) => SUPPORTED_COUNTRY_CODES.includes(v), 'Desteklenmeyen ülke kodu');
